import {
  Box,
  Button,
  Collapse,
  Code,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiChevronDown, FiChevronUp, FiSearch } from 'react-icons/fi'
import type { IdLookupEntity, IdLookupResult } from '@/types'
import { lookupService } from '@/services/admin/lookupService'
import { useToast } from '@/hooks/useToast'

interface IdLookupPanelProps {
  entity: IdLookupEntity
}

const getErrorMessage = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const response = (error as any).response
  if (response?.status === 404) {
    return 'notFound'
  }
  const data = response?.data
  const message = typeof data?.error === 'string'
    ? data.error
    : data?.error?.message ?? data?.message
  return typeof message === 'string' && message.trim() ? message : undefined
}

export function IdLookupPanel({ entity }: IdLookupPanelProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true })
  const [inputValue, setInputValue] = useState('')
  const [result, setResult] = useState<IdLookupResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const panelBg = useColorModeValue('white', 'gray.800')
  const mutedText = useColorModeValue('gray.600', 'gray.400')

  const handleLookup = async () => {
    const trimmed = inputValue.trim()
    if (!trimmed) {
      toast.warning({ titleKey: 'idLookup.errors.required' })
      setResult(null)
      return
    }

    setIsLoading(true)
    setResult(null)

    try {
      const data = await lookupService.lookupId(entity, trimmed)
      setResult(data)
    } catch (error) {
      const messageKey = getErrorMessage(error)
      if (messageKey === 'notFound') {
        toast.warning({ titleKey: 'idLookup.errors.notFound' })
      } else {
        toast.error(messageKey ? { title: messageKey } : { titleKey: 'idLookup.errors.failed' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="md" borderColor={borderColor} bg={panelBg}>
      <HStack justify="space-between" align="center" px={4} py={2} flexWrap="wrap" gap={2}>
        <Box flex="1" minW={0}>
          <Text fontWeight="semibold" fontSize="sm">
            {t('idLookup.title')}
          </Text>
          <Text fontSize="xs" color={mutedText} lineHeight="1.3">
            {t('idLookup.description')}
          </Text>
        </Box>
        <Button
          size="xs"
          variant="ghost"
          onClick={onToggle}
          rightIcon={isOpen ? <FiChevronUp /> : <FiChevronDown />}
          whiteSpace="normal"
          h="auto"
          minH={6}
        >
          {isOpen ? t('idLookup.collapse') : t('idLookup.expand')}
        </Button>
      </HStack>
      <Collapse in={isOpen} animateOpacity>
        <Box px={4} pb={3}>
          <VStack align="stretch" spacing={2}>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>
                {t('idLookup.label')}
              </FormLabel>
              <Stack direction={{ base: 'column', sm: 'row' }} spacing={2}>
                <Input
                  size="sm"
                  value={inputValue}
                  onChange={(event) => {
                    setInputValue(event.target.value)
                    setResult(null)
                  }}
                  placeholder={t('idLookup.placeholder')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      void handleLookup()
                    }
                  }}
                />
                <Button
                  colorScheme="blue"
                  leftIcon={<FiSearch />}
                  size="sm"
                  isLoading={isLoading}
                  onClick={() => void handleLookup()}
                  w={{ base: 'full', sm: 'auto' }}
                >
                  {t('idLookup.action')}
                </Button>
              </Stack>
            </FormControl>

            {result && (
              <Box borderWidth="1px" borderRadius="md" borderColor={borderColor} px={2} py={2}>
                <VStack align="stretch" spacing={1}>
                  <HStack spacing={3}>
                    <Text fontSize="xs" color={mutedText} minW="100px">
                      {t('idLookup.internalId')}
                    </Text>
                    <Code fontSize="xs">{result.internalId}</Code>
                  </HStack>
                  <HStack spacing={3}>
                    <Text fontSize="xs" color={mutedText} minW="100px">
                      {t('idLookup.businessId')}
                    </Text>
                    <Code fontSize="xs">{result.businessId}</Code>
                  </HStack>
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  )
}
